import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth, homePathFor } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, KeyRound, Smartphone } from "lucide-react";

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("credentials"); // credentials | mfa | setup | recovery
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [loggedUser, setLoggedUser] = useState(null);

  const goHome = (user) => navigate(homePathFor(user), { replace: true });

  const submitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("mfa");
      } else if (data.mfa_setup_required) {
        setMfaToken(data.mfa_token);
        const res = await api.post("/auth/mfa/setup", {}, { headers: { Authorization: `Bearer ${data.mfa_token}` } });
        setQrCode(res.data.qr_code);
        setSecret(res.data.secret);
        setStep("setup");
      } else {
        setUser(data.user);
        toast.success("Connexion réussie");
        goHome(data.user);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code }, { headers: { Authorization: `Bearer ${mfaToken}` } });
      setUser(data.user);
      toast.success("Connexion réussie");
      goHome(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitEnable = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code }, { headers: { Authorization: `Bearer ${mfaToken}` } });
      setRecoveryCodes(data.recovery_codes);
      setUser(data.user);
      setLoggedUser(data.user);
      setStep("recovery");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f8fafc]">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1569758884145-6a2e8a00e539?q=85&w=1600"
          alt="Architecture d'église"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#075985]/80" />
        <div className="relative z-10 flex flex-col justify-center p-16 text-white">
          <img src="/assets/favicon.png" alt="Logo du Diocèse de Pamiers" className="h-24 w-24 mb-8" />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: "Work Sans" }}>Notes de frais</h1>
          <p className="text-xl text-sky-100 max-w-md leading-relaxed">
            Diocèse de Pamiers — déposez vos notes de frais simplement, depuis votre téléphone ou votre ordinateur.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-enter">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/assets/favicon.png" alt="Logo du Diocèse de Pamiers" className="h-14 w-14" />
            <div>
              <h1 className="text-2xl font-bold text-[#0369a1]" style={{ fontFamily: "Work Sans" }}>Notes de frais</h1>
              <p className="text-slate-500 font-medium">Diocèse de Pamiers</p>
            </div>
          </div>

          {step === "credentials" && (
            <form onSubmit={submitLogin} className="card-base" data-testid="login-form">
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">Connexion</h2>
              <p className="text-slate-600 mb-6">Saisissez votre email et votre mot de passe.</p>
              <div className="space-y-5">
                <div>
                  <Label htmlFor="email" className="label-lg">Email ou numéro de téléphone</Label>
                  <Input id="email" type="text" required autoComplete="username" value={email}
                    onChange={(e) => setEmail(e.target.value)} className="input-lg"
                    placeholder="prenom.nom@diocese-pamiers.fr ou 06…" data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="password" className="label-lg">Mot de passe</Label>
                  <Input id="password" type="password" required autoComplete="current-password" value={password}
                    onChange={(e) => setPassword(e.target.value)} className="input-lg"
                    placeholder="Votre mot de passe" data-testid="login-password-input" />
                </div>
                {error && <p className="text-[#b91c1c] font-semibold text-base" role="alert" data-testid="login-error">{error}</p>}
                <button type="submit" disabled={loading} className="btn-primary-lg w-full" data-testid="login-submit-button">
                  <KeyRound className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
                  {loading ? "Connexion en cours…" : "Se connecter"}
                </button>
                <p className="text-center">
                  <Link to="/mot-de-passe-oublie" className="text-[#0369a1] font-semibold underline underline-offset-4" data-testid="forgot-password-link">
                    J'ai oublié mon mot de passe
                  </Link>
                </p>
              </div>
            </form>
          )}

          {step === "mfa" && (
            <form onSubmit={submitMfa} className="card-base" data-testid="mfa-verify-form">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="h-10 w-10 text-[#0369a1]" aria-hidden="true" strokeWidth={2.5} />
                <h2 className="text-2xl font-semibold text-slate-900">Vérification en 2 étapes</h2>
              </div>
              <p className="text-slate-600 mb-6">
                Ouvrez votre application d'authentification et saisissez le code à 6 chiffres.
                Vous pouvez aussi utiliser un code de secours.
              </p>
              <Label htmlFor="mfa-code" className="label-lg">Code à 6 chiffres</Label>
              <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code}
                onChange={(e) => setCode(e.target.value)} className="input-lg text-center text-2xl tracking-[0.5em]"
                placeholder="000000" data-testid="mfa-code-input" />
              {error && <p className="text-[#b91c1c] font-semibold mt-4" role="alert" data-testid="mfa-error">{error}</p>}
              <button type="submit" disabled={loading || !code} className="btn-primary-lg w-full mt-6" data-testid="mfa-verify-button">
                {loading ? "Vérification…" : "Valider le code"}
              </button>
            </form>
          )}

          {step === "setup" && (
            <form onSubmit={submitEnable} className="card-base" data-testid="mfa-setup-form">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="h-10 w-10 text-[#0369a1]" aria-hidden="true" strokeWidth={2.5} />
                <h2 className="text-2xl font-semibold text-slate-900">Sécurisez votre compte</h2>
              </div>
              <p className="text-slate-600 mb-4">
                Votre rôle exige une double authentification. Scannez ce QR code avec une application
                comme <strong>Google Authenticator</strong> ou <strong>Microsoft Authenticator</strong>.
              </p>
              {qrCode && (
                <div className="flex justify-center my-6">
                  <img src={qrCode} alt="QR code de configuration de la double authentification" className="h-56 w-56 border-2 border-slate-200 rounded-xl" data-testid="mfa-qr-code" />
                </div>
              )}
              <p className="text-sm text-slate-500 mb-4 break-all">Clé manuelle : <code className="font-mono bg-slate-100 px-2 py-1 rounded" data-testid="mfa-secret">{secret}</code></p>
              <Label htmlFor="setup-code" className="label-lg">Saisissez le code affiché par l'application</Label>
              <Input id="setup-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)}
                className="input-lg text-center text-2xl tracking-[0.5em]" placeholder="000000" data-testid="mfa-setup-code-input" />
              {error && <p className="text-[#b91c1c] font-semibold mt-4" role="alert" data-testid="mfa-setup-error">{error}</p>}
              <button type="submit" disabled={loading || !code} className="btn-primary-lg w-full mt-6" data-testid="mfa-enable-button">
                {loading ? "Activation…" : "Activer la double authentification"}
              </button>
            </form>
          )}

          {step === "recovery" && recoveryCodes && (
            <div className="card-base" data-testid="recovery-codes-panel">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">Vos codes de secours</h2>
              <p className="text-slate-600 mb-4">
                <strong>Notez ces codes et conservez-les en lieu sûr.</strong> Ils vous permettront de vous
                connecter si vous perdez votre téléphone. Chaque code ne peut être utilisé qu'une seule fois.
              </p>
              <div className="grid grid-cols-2 gap-2 my-6">
                {recoveryCodes.map((c) => (
                  <code key={c} className="font-mono text-lg bg-slate-100 border border-slate-300 rounded-lg px-3 py-2 text-center" data-testid={`recovery-code-${c}`}>{c}</code>
                ))}
              </div>
              <button onClick={() => goHome(loggedUser)} className="btn-primary-lg w-full" data-testid="recovery-continue-button">
                J'ai noté mes codes — Continuer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
